import type { DragOverEvent as ReactDragOverEvent } from '@dnd-kit/react';

import type { BlockStackLayoutConfig } from '@/lib/blocks/layout/block-stack-layout';
import { BLOCK_STACK_DIRECTION_DEFAULT } from '@/lib/blocks/layout/block-stack-layout';
import type { GroupDirection } from '@/lib/blocks/layout/group-layout';
import type { PageBlock } from '@/types/block';
import {
  canResolveDropResult,
  type DropIndicator,
  type DropValidationEventInput,
  getSameParentReorderPreview,
} from '../core';
import { resolveDropOperation } from './drop-operation';

export interface DragOverDecision {
  indicator: DropIndicator | null;
  invalidDropFeedback: boolean;
  reorderPreview: { parentId: string | null; items: string[] } | null;
}

type ComputeDragOverDecisionProps = {
  event: DropValidationEventInput;
  blocks: PageBlock[];
  blockMap: Map<string, PageBlock>;
  stableIdByPersistedId: Map<string, string>;
  childrenByParent: Map<string, PageBlock[]>;
  rootParentDirection?: GroupDirection;
  rootStackLayout?: BlockStackLayoutConfig;
  previousIndicator: DropIndicator | null;
  structuralRegionRootByBlockId?: ReadonlyMap<string, string>;
  protectedBlockIds?: ReadonlySet<string>;
};

export const computeDragOverDecision = (props: ComputeDragOverDecisionProps): DragOverDecision => {
  const {
    event,
    blocks,
    blockMap,
    stableIdByPersistedId,
    childrenByParent,
    rootParentDirection,
    rootStackLayout,
    previousIndicator,
    structuralRegionRootByBlockId,
    protectedBlockIds,
  } = props;
  const operation = resolveDropOperation({
    event,
    blocks,
    blockMap,
    stableIdByPersistedId,
    previousIndicator,
    rootParentDirection,
  });
  const canDropAtIndicator =
    operation.indicator !== null &&
    canResolveDropResult({
      event,
      blocks,
      blockMap,
      childrenByParent,
      dropIndicator: operation.indicator,
      rootParentDirection: rootParentDirection ?? BLOCK_STACK_DIRECTION_DEFAULT,
      rootStackLayout,
      structuralRegionRootByBlockId,
      protectedBlockIds,
    });
  const indicator = canDropAtIndicator ? operation.indicator : null;

  let invalidDropFeedback = false;
  switch (operation.dropSurface) {
    case 'none':
      invalidDropFeedback = true;
      break;
    case 'insert-gap-zone':
    case 'group-gap-zone':
    case 'parent-drop':
      invalidDropFeedback = !canDropAtIndicator;
      break;
    case 'block':
      invalidDropFeedback = operation.indicator ? !canDropAtIndicator : false;
      break;
    default:
      invalidDropFeedback = true;
      break;
  }

  const indicatorChanged =
    indicator?.targetId !== previousIndicator?.targetId ||
    indicator?.edge !== previousIndicator?.edge;
  const reorderPreview =
    indicatorChanged && indicator
      ? getSameParentReorderPreview({
          event: event as ReactDragOverEvent,
          blocks,
          blockMap,
          childrenByParent,
          dropIndicator: indicator,
          rootParentDirection: rootParentDirection ?? BLOCK_STACK_DIRECTION_DEFAULT,
          rootStackLayout,
          structuralRegionRootByBlockId,
          protectedBlockIds,
        })
      : null;

  return {
    indicator,
    invalidDropFeedback,
    reorderPreview,
  };
};
